import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { useQuery } from "@apollo/client";
import { Select } from "antd";
import classNames from "classnames";
import { EVA_RESEARCH_NAME, IS_DEV_RESEARCH_ENV } from "constants/app.constants";
import {
  FORECAST_FOLDER_ID_TO_FORECAST_SOURCE_MAP,
  FORECAST_TRUMPING_FOLDER_ID,
  FORECAST_TRUMPING_FOLDER_NAME,
  MCDANIEL_SEARCH_FOLDER_NAME,
  NOVI_FORECAST_SOURCE,
  NOVI_SEARCH_FOLDER_NAME,
  USER_ARPS,
  WORKING_FORECAST_FOLDER_NAME,
  WORKING_FORECAST_SOURCE
} from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import {
  selectUseMcDanielForecasts,
  selectUseNoviForecasts,
  selectUseWorkingForecasts,
  useForecastStore
} from "stores/forecast";
import styled from "styled-components";

import { FOLDERS_LIGHT } from "api/userArps";

import { useHasNoviAccess } from "components/arps/hooks/useHasNoviAccess";
import { useSelectedProject } from "components/project/projects/hooks";

import { numberToRescatString } from "../sync/util";
import { useUserDefaults } from "../user/hooks";
import { useGroupByStore } from "./GroupByStoreProvider";
import { selectProps, selectSelectedForecastFolder } from "./groupBySelectors";
import { getForecastSourceFromSelection } from "./helpers";

interface ProjectForecast {
  name: string;
  folderId: string;
  rescat: string[];
}

interface ForecastSourceSelectModel {
  showLabel?: boolean;
  showGroup?: boolean;
  isFocused?: boolean;
  rescatFocused?: boolean;
  onSelectRef?: (index: number, selectEl: Maybe<HTMLElement>) => void;
  componentIndex?: number;
  rescatComponentIndex?: number;
  alwaysShowForecastTrumping?: boolean;
  hideForecastTrumping?: boolean;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export default function ForecastSourceSelect({
  showGroup = false,
  showLabel = true,
  isFocused = false,
  rescatFocused = false,
  onSelectRef,
  componentIndex = 0,
  rescatComponentIndex = 1,
  alwaysShowForecastTrumping = false,
  hideForecastTrumping = false
}: ForecastSourceSelectModel) {
  const selectRef = useRef<any>(null);
  const rescatSelectRef = useRef<any>(null);
  const contextDefaultFolder = useGroupByStore(selectSelectedForecastFolder);
  const selectedCategory = useGroupByStore((s) => s.selectedCategory);
  const groupByProps = useGroupByStore(selectProps);
  const setSelectedForecastFolderAction = useGroupByStore(
    (s) => s.setSelectedForecastFolder
  );
  const setSelectedForecastFolderNameAction = useGroupByStore(
    (s) => s.setSelectedForecastFolderName
  );
  const setSelectedRescatAction = useGroupByStore((s) => s.setSelectedRescat);
  const userDefaults = useUserDefaults();
  const hasNoviAccess = useHasNoviAccess();
  const { selectedProject: project } = useSelectedProject();
  const userSync = useSelector((state: RootState) => state.app.syncWells);
  const forecastMode = useSelector((state: RootState) => state.app.forecastMode);
  const useMcDanielForecasts = useForecastStore(selectUseMcDanielForecasts);
  const useNoviForecasts = useForecastStore(selectUseNoviForecasts);
  const useWorkingForecasts = useForecastStore(selectUseWorkingForecasts);
  const [forecastList, setForecastList] = useState<ProjectForecast[]>([]);
  const [selectedForecastFolder, setSelectedForecastFolder] = useState("");
  const [selectedForecastFolderName, setSelectedForecastFolderName] = useState("");
  const [selectedRescat, setSelectedRescat] = useState("");
  const [forecastRescats, setForecastRescats] = useState([]);

  // What the project Forecasts panel currently implies for this dropdown. Novi is filtered
  // through entitlement first: the panel boolean can carry a stale Novi selection (a restored
  // View, or a saved default from when the org had Novi), and acting on it would put Novi in
  // the outgoing request for an org that is not entitled to it.
  const derivedForecastFolderId = getForecastSourceFromSelection({
    forecastMode,
    useMcDanielForecasts,
    useNoviForecasts: useNoviForecasts && hasNoviAccess,
    useWorkingForecasts
  });

  // The last panel-derived value we acted on, so we can tell "the panel selection changed"
  // apart from "this effect re-ran for some other reason". Starts undefined rather than null
  // so the first run counts as "nothing changed yet" — null is a real derived value.
  const lastDerivedForecastFolderIdRef = useRef<Maybe<string>>(undefined);
  // The value the user picked in this dropdown, held so a re-run does not silently undo it.
  const userChosenForecastFolderRef = useRef<Nullable<string>>(null);

  const { data: foldersData } = useQuery(FOLDERS_LIGHT, {
    variables: {
      req: {
        projectId: project?.projectId ?? "",
        type: USER_ARPS
      }
    },
    skip: !project?.projectId
  });

  function onlyUnique(value, index, self) {
    return self.indexOf(value) === index;
  }
  useEffect(() => {
    if (!foldersData) {
      return;
    }

    // Performance fields only, per the revised EVA-104 scope. Forecast-category fields were
    // offered trumping in the original implementation because qualifyPropertyWithForecast
    // applies it to both categories, so it was reachable — but the ticket now asks for
    // Performance only, so the option is not offered there.
    const showForecastTrumping =
      !hideForecastTrumping &&
      (alwaysShowForecastTrumping || selectedCategory?.name === "Performance");

    const list: ProjectForecast[] = [
      ...(showForecastTrumping
        ? [
            {
              name: FORECAST_TRUMPING_FOLDER_NAME,
              folderId: FORECAST_TRUMPING_FOLDER_ID,
              rescat: ["P+PDP"]
            }
          ]
        : []),
      {
        name: EVA_RESEARCH_NAME,
        folderId: MCDANIEL_SEARCH_FOLDER_NAME,
        rescat: ["P+PDP"]
      },
      // Only orgs with a Novi subscription should see the Novi forecast source (EVA-6847)
      ...(hasNoviAccess
        ? [
            {
              name: NOVI_SEARCH_FOLDER_NAME,
              folderId: NOVI_FORECAST_SOURCE,
              rescat: ["P+PDP"]
            }
          ]
        : [])
    ];

    if (IS_DEV_RESEARCH_ENV) {
      // Working forecast folder is only available in dev and research env
      list.push({
        name: WORKING_FORECAST_FOLDER_NAME,
        folderId: WORKING_FORECAST_SOURCE,
        rescat: ["P+PDP"]
      });
    }

    const additionalForecastFolders = foldersData.folders.map(
      (fc: any) =>
        ({
          name: fc.name,
          folderId: fc.folderId,
          rescat: fc.forecasts.map((f) => f.reserveCategory).filter(onlyUnique)
        } as ProjectForecast)
    );

    list.push(...additionalForecastFolders);

    // Changing what is ticked in the Forecasts panel is a fresh instruction, so it outranks
    // an earlier pick in this dropdown. Any other reason this effect re-ran is not.
    const panelSelectionChanged =
      lastDerivedForecastFolderIdRef.current !== undefined &&
      derivedForecastFolderId !== lastDerivedForecastFolderIdRef.current;
    lastDerivedForecastFolderIdRef.current = derivedForecastFolderId;
    if (panelSelectionChanged) {
      userChosenForecastFolderRef.current = null;
    }

    // EVA Research only is the forecast store's own initial state, so before anything has
    // hydrated it, it is indistinguishable from a panel nobody has touched. Preferring it over
    // the saved default in that window would flash EVA Research at a user whose saved default
    // is Novi, until project init hydrates the store. A just-changed panel is a real
    // instruction whatever it says, so that still counts.
    const hasPanelSignal =
      panelSelectionChanged || derivedForecastFolderId !== MCDANIEL_SEARCH_FOLDER_NAME;

    // The derived value only counts if this dropdown actually offers it. Categories differ:
    // Pad Metrics offers no Forecast Trumping option, so a both-ticked panel would resolve to
    // "-3", match nothing, and hit the no-match fallback below — discarding the user's saved
    // default in favour of EVA Research. Falling through to the saved default is the honest
    // answer when the panel names something this dropdown cannot show.
    const isDerivedOffered =
      !!derivedForecastFolderId &&
      list.some((f: ProjectForecast) => f.folderId === derivedForecastFolderId);

    const userDefaultForecastFolderId =
      userDefaults?.forecastSourceSetting?.forecastFolderId;
    // folderId in this list of folders is actually the "forecastSource" whereas the userDefaults is the actual folderId.
    // which is why we need to map it
    let selectedForecastFolderId =
      (hasPanelSignal && isDerivedOffered ? derivedForecastFolderId : null) ??
      FORECAST_FOLDER_ID_TO_FORECAST_SOURCE_MAP[userDefaultForecastFolderId];

    // If the axis already has a forecastFolder set (e.g. carried over from a chart type
    // switch), prefer that over the user default so the dropdown matches the actual request.
    // The panel wins over it in two cases. A just-changed selection, because that is the more
    // recent instruction. And both sources ticked, because trumping is then the only source
    // that describes what the panel actually holds — an axis committed under a single source
    // would otherwise keep that source forever on charts saved before the second was ticked.
    // Either way it only wins if the panel named something this dropdown can show.
    const panelOverridesAxis =
      isDerivedOffered &&
      (panelSelectionChanged || derivedForecastFolderId === FORECAST_TRUMPING_FOLDER_ID);
    const existingFolder = groupByProps?.chartTypeParam?.forecastFolder;
    if (existingFolder && !panelOverridesAxis) {
      const matchesListEntry = list.some(
        (f: ProjectForecast) => f.folderId === existingFolder
      );
      if (matchesListEntry) {
        selectedForecastFolderId = existingFolder;
      }
    }

    // If the GroupByProvider set a default (e.g. forecastTrumping for axis-selector charts)
    // and there's no explicit user default, use the context default
    if (!selectedForecastFolderId && contextDefaultFolder) {
      selectedForecastFolderId = contextDefaultFolder;
    }

    let selectedForecastFolderName = list.find(
      (f: ProjectForecast) => f.folderId === selectedForecastFolderId
    )?.name;

    // If a folder was resolved but is no longer available (e.g. a persisted Novi
    // default for an org that lost Novi access), fall back to McDaniel rather than
    // leaving the select on a value that matches no option (EVA-6847).
    if (selectedForecastFolderId && !selectedForecastFolderName) {
      selectedForecastFolderId = MCDANIEL_SEARCH_FOLDER_NAME;
      selectedForecastFolderName = EVA_RESEARCH_NAME;
    }

    let isForcedBySync = false;
    if (userSync?.schema) {
      // Check if this schema already exists in the list
      const schemaExists = list.some((item) => item.folderId === userSync.schema);

      if (!schemaExists) {
        list.push({
          name: userSync.schema,
          folderId: userSync.schema,
          rescat: [numberToRescatString(userSync.forecastRescat)]
        });
      }

      if (userSync?.isSync && userSync.useFcstFromClient) {
        selectedForecastFolderId = userSync.schema;
        selectedForecastFolderName = userSync.schema;
        isForcedBySync = true;
      }
    }
    setForecastList(list);

    // A pick made in this dropdown stands until the panel selection changes. It is dropped if
    // the option it named is no longer offered — switching to a category that does not list
    // Forecast Trumping, say — because a value with no matching option shows as raw text.
    const userChoice = userChosenForecastFolderRef.current;
    const isUserChoiceStillOffered =
      !!userChoice && list.some((f: ProjectForecast) => f.folderId === userChoice);
    if (!isForcedBySync && isUserChoiceStillOffered) {
      return;
    }
    userChosenForecastFolderRef.current = null;

    setSelectedForecastFolder(selectedForecastFolderId);
    setSelectedForecastFolderName(selectedForecastFolderName);
  }, [
    foldersData,
    userSync,
    userDefaults,
    groupByProps?.chartType,
    selectedCategory,
    alwaysShowForecastTrumping,
    hideForecastTrumping,
    hasNoviAccess,
    derivedForecastFolderId
  ]);

  useEffect(() => {
    if (!selectedForecastFolder) {
      return;
    }
    const forecast = forecastList.find((fc) => fc.folderId === selectedForecastFolder);
    if (!forecast) {
      return;
    }
    setForecastRescats(
      forecast.rescat.map((rescat) => ({ label: rescat, value: rescat }))
    );
    if (forecast.rescat) {
      setSelectedRescat(forecast.rescat[0]);
    }
    setSelectedForecastFolderAction(selectedForecastFolder);
    setSelectedForecastFolderNameAction(selectedForecastFolderName);
  }, [
    selectedForecastFolder,
    forecastList,
    setSelectedForecastFolderAction,
    setSelectedForecastFolderNameAction
  ]);

  useEffect(() => {
    if (!selectedForecastFolder || !selectedRescat) {
      return;
    }
    setSelectedRescatAction(selectedRescat);
  }, [selectedForecastFolder, selectedRescat, setSelectedRescatAction]);

  return (
    <div>
      {showLabel && <label>Forecast Source</label>}
      <Wrapper>
        <Select
          ref={(el) => {
            selectRef.current = el;
            if (onSelectRef) onSelectRef(componentIndex, el as HTMLElement);
          }}
          className={classNames("toolbar-item", {
            "keyboard-focused": isFocused
          })}
          data-testid="forecast-source-select"
          popupClassName="modal-select"
          value={selectedForecastFolder}
          onChange={(value, label) => {
            userChosenForecastFolderRef.current = value;
            setSelectedForecastFolder(value);
            setSelectedForecastFolderName(Object.values(label)[1]);
            // Blur the select after selection
            if (selectRef.current) {
              selectRef.current.blur();
            }
          }}
          options={
            showGroup
              ? ([
                  {
                    label: "Forecast Source",
                    options: forecastList.map((fc) => ({
                      value: fc.folderId,
                      label: fc.name
                    }))
                  }
                ] as any)
              : forecastList.map((fc) => ({
                  value: fc.folderId,
                  label: fc.name
                }))
          }
        />
        <RescatWrapper>
          <Select
            ref={(el) => {
              rescatSelectRef.current = el;
              if (onSelectRef) onSelectRef(rescatComponentIndex, el as HTMLElement);
            }}
            className={classNames("toolbar-item", {
              "keyboard-focused": rescatFocused
            })}
            value={selectedRescat}
            options={[{ label: "Rescat", options: forecastRescats }]}
            onChange={(value) => {
              setSelectedRescat(value);
              // Blur the select after selection
              if (rescatSelectRef.current) {
                rescatSelectRef.current.blur();
              }
            }}
            popupClassName="modal-select"
          />
        </RescatWrapper>
      </Wrapper>
    </div>
  );
}

const Wrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-content: center;
  gap: var(--space-2);
`;

const RescatWrapper = styled.div`
  max-width: 95px;
  width: 95px;

  > div {
    width: 100%;
  }
`;
